import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../config/authConfig';
import { useHasRole } from '../../hooks/useHasRole';
import { useActionableApprovalsCount, useCurrentUser } from '../../hooks/useQueries';
import './sidebar.css';

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const [barTop, setBarTop] = useState(0);
  const [showText, setShowText] = useState(!collapsed);
  const linkRefs = useRef({});
  const { hasRole: isAdmin } = useHasRole('admin');
  const { instance, accounts, inProgress } = useMsal();
  
  // Fetch user info
  const { data: userInfo } = useCurrentUser();

  const { data: actionableApprovalsCountData } = useActionableApprovalsCount();
  const hasActionableApprovals = Number(actionableApprovalsCountData?.count || 0) > 0;

  const isSignedIn = accounts && accounts.length > 0;
  const currentAccount = isSignedIn ? (accounts[0] || null) : null;
  const canShowSignIn = !isSignedIn && inProgress === 'none';
  const canSwitchAccount = isSignedIn && inProgress === 'none';
  const canSignOut = isSignedIn && inProgress === 'none';

  const displayName = userInfo?.name
    || currentAccount?.name
    || currentAccount?.username
    || (isSignedIn ? 'Signed in' : 'Not signed in');

  const handleSignIn = async () => {
    try {
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'select_account'
      });
    } catch (error) {
      console.error('Sidebar sign-in redirect failed:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Sidebar sign-out redirect failed:', error);
    }
  };

  const toggleSidebar = () => {
    const nextCollapsed = !collapsed;
    onToggle();
    if (!nextCollapsed) {
      // Expanding: delay text until transition ends
      setTimeout(() => setShowText(true), 50);
    } else {
      // Collapsing: hide text immediately
      setShowText(false);
    }
  };

  const navItems = useMemo(() => {
    const allNavItems = [
      { path: '/brainstorming', icon: 'bi-lightbulb', label: 'Brainstorming' },
      { 
        path: '/items', 
        icon: 'bi-clipboard-check', 
        label: 'Items',
        submenus: [
          { path: '/my-items', icon: 'bi-person-check', label: 'My Items' }
        ]
      },
      { 
        path: '/tasks', 
        icon: 'bi-list-task', 
        label: 'Tasks',
        submenus: [
          { path: '/my-tasks', icon: 'bi-person-check', label: 'My Tasks' }
        ]
      },
      { path: '/approvals', icon: 'bi-shield-check', label: 'Approvals' },
      { path: '/reports', icon: 'bi-bar-chart', label: 'Reports' },
      { path: '/admin-settings', icon: 'bi-boxes', label: 'Admin' },
    ];
    
    // Filter out Settings for non-admin users
    return allNavItems.filter(item => {
      if (item.path === '/admin-settings') {
        return isAdmin;
      }
      return true;
    });
  }, [isAdmin]);

  useEffect(() => {
    let normalizedPath = location.pathname;
    // Handle user-settings path for the new user settings button
    if (normalizedPath === '/user-settings') {
      normalizedPath = '/user-settings';
    }
    
    let refToUse = linkRefs.current[normalizedPath];

    if (refToUse) {
      setBarTop(refToUse.offsetTop);
    }
  }, [location.pathname, collapsed, navItems]);

  return (
    <div className={`sidebar position-relative ${collapsed ? 'collapsed' : ''}`}>
      <div className="logo-container d-flex align-items-center">
         <Link to="/brainstorming">
          <img src="/res/nsi-logo-flat-white.png" className="logo-img" alt="Logo" style={{ cursor: 'pointer' }} />
        </Link>
        {!collapsed && <span className="company-name">STJV-RIIMS</span>}
        <button
          type="button"
          className="toggle-btn-container ms-auto"
          onClick={toggleSidebar}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <i className={`bi bi-chevron-left toggle-btn ${collapsed ? 'rotated' : ''}`}></i>
        </button>
      </div>

      <div className="position-relative">
        <div 
          className="active-slide-bar" 
          style={{ 
            top: barTop,
            width: (location.pathname !== '/settings') ? '4px' : '0',
          }} 
        />
        <ul className="nav flex-column pt-3 main-links">
          {navItems.map(({ path, icon, label, submenus }) => {
            const isActive = location.pathname === path;
            // Also highlight parent if we're on a submenu route
            const isParentActive = submenus?.some(sub => sub.path === location.pathname);

            return (
              <React.Fragment key={path}>
                <li
                  className="nav-item"
                  ref={(el) => (linkRefs.current[path] = el)}
                >
                  <Link
                    to={path}
                    className={`nav-link ${isActive || isParentActive ? 'fw-bold' : ''}`}
                    aria-label={
                      path === '/approvals' && hasActionableApprovals
                        ? `${label} (action required)`
                        : label
                    }
                  >
                    <i className={`bi ${icon} nav-icon me-2`}></i>
                    {path === '/approvals' && hasActionableApprovals && (
                      <span className="sidebar__badge-dot" aria-hidden="true" />
                    )}
                    {!collapsed && <span className="link-text" style={{
                      opacity: showText ? 1 : 0,
                      transition: 'opacity 0.3s',
                      pointerEvents: showText ? 'auto' : 'none'
                    }}>{label}</span>}
                  </Link>
                </li>
                
                {/* Render submenus if they exist */}
                {submenus && submenus.map((submenu) => (
                  <li
                    key={submenu.path}
                    className="nav-item nav-submenu"
                    ref={(el) => (linkRefs.current[submenu.path] = el)}
                  >
                    <Link to={submenu.path} className={`nav-link nav-submenu-link ${location.pathname === submenu.path ? 'fw-bold' : ''}`}>
                      <i className={`bi ${submenu.icon} nav-icon me-2`}></i>
                      {!collapsed && <span className="link-text" style={{
                        opacity: showText ? 1 : 0,
                        transition: 'opacity 0.3s',
                        pointerEvents: showText ? 'auto' : 'none'
                      }}>{submenu.label}</span>}
                    </Link>
                  </li>
                ))}
              </React.Fragment>
            );
          })}
        </ul>
      </div>

      {/* Help Guide, User Settings and Trash buttons at bottom */}
      <div className="user-settings-section">
        <ul className="nav flex-column">
          <li
            className="nav-item"
            ref={(el) => (linkRefs.current['/help-guide'] = el)}
          >
            <Link
              to="/help-guide"
              className={`nav-link ${
                location.pathname === '/help-guide' || location.pathname === '/help' ? 'fw-bold active' : ''
              }`}
            >
              <i className={`bi bi-question-circle nav-icon me-2 ${
                location.pathname === '/help-guide' || location.pathname === '/help' ? 'fw-bold' : ''
              }`}></i>
              {!collapsed && (
                <span
                  className="link-text"
                  style={{
                    opacity: showText ? 1 : 0,
                    transition: 'opacity 0.3s',
                    pointerEvents: showText ? 'auto' : 'none',
                  }}
                >
                  Help Guide
                </span>
              )}
            </Link>
          </li>

          <li
            className="nav-item"
            ref={(el) => (linkRefs.current['/user-settings'] = el)}
          >
            <Link to="/user-settings" className={`nav-link ${
              location.pathname === '/user-settings' ? 'fw-bold active' : ''
            }`}>
              <i className={`bi bi-person-gear nav-icon me-2 ${
                location.pathname === '/user-settings' ? 'fw-bold' : ''
              }`}></i>
              {!collapsed && (
                <span className="link-text" style={{
                  opacity: showText ? 1 : 0,
                  transition: 'opacity 0.3s',
                  pointerEvents: showText ? 'auto' : 'none'
                }}>User Settings</span>
              )}
            </Link>
          </li>

          <li
            className="nav-item"
            ref={(el) => (linkRefs.current['/trashcan'] = el)}
          >
            <Link to="/trashcan" className={`nav-link ${
              location.pathname === '/trashcan' ? 'fw-bold active' : ''
            }`}>
              <i className={`bi bi-trash nav-icon me-2 ${
                location.pathname === '/trashcan' ? 'fw-bold' : ''
              }`}></i>
              {!collapsed && (
                <span className="link-text" style={{
                  opacity: showText ? 1 : 0,
                  transition: 'opacity 0.3s',
                  pointerEvents: showText ? 'auto' : 'none'
                }}>Trash</span>
              )}
            </Link>
          </li>
        </ul>
      </div>

      {/* User info at bottom */}
      <div className="user-info-section" style={{
        fontSize: collapsed ? '0.8rem' : '1rem',
      }}>
        {/* Show profile picture and initials when collapsed, show picture and name when expanded */}
        {userInfo && userInfo.picture ? (
          <img
            src={userInfo.picture}
            alt="Profile"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <i className="bi bi-person-circle" style={{ fontSize: '2rem' }}></i>
        )}
        {collapsed && userInfo && userInfo.name && (
          <span className="user-initials">
            {userInfo.name.split(' ').map(n => n[0]).join('.').toUpperCase()}.
          </span>
        )}
        {!collapsed && (
          <div className="user-name-display" style={{ 
            opacity: showText ? 1 : 0,
            transition: 'opacity 0.3s',
            pointerEvents: showText ? 'auto' : 'none'
          }}>
            <div>
              {displayName}
            </div>
            {canShowSignIn && (
              <button
                type="button"
                className="sidebar-signin-btn"
                onClick={handleSignIn}
              >
                Sign in
              </button>
            )}
            {canSwitchAccount && !userInfo?.name && (
              <button
                type="button"
                className="sidebar-signin-btn"
                onClick={handleSignIn}
              >
                Switch account
              </button>
            )}
            {canSignOut && (
              <button
                type="button"
                className="sidebar-signout-btn"
                onClick={handleSignOut}
              >
                Logout
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}